/**
 * levelgen/difficulty.ts — What "hard" means, in numbers.
 *
 * Two halves that deliberately face opposite directions:
 *
 *   BUDGETS   `budgetFor(difficulty)` — the knobs the generator is allowed to
 *             turn for a band: how much town to build, how much traffic to put
 *             on it, how much wildlife to let loose.
 *   RATING    `rateLevel(spec)` — how hard a level actually came out, measured
 *             from the finished level.
 *
 * The second is not derived from the first, and that is the entire design. A
 * generator that sets `difficulty: 'hard'` and then declares the level hard has
 * proved nothing; the same budget can produce a taxing level or a stroll
 * depending on where the seed happened to put the bin. So the generator builds
 * to a budget, *re-measures* the result, and only ships a level whose measured
 * band matches the one it was asked for — nudging the continuous knobs until it
 * does, or throwing the seed away (see `generate.ts` → `tune`).
 *
 * ## What the rating measures
 *
 * Only things a player feels, and only things that survive the generator:
 *
 *   traffic    how often a vehicle comes, how many at once, how fast
 *   critters   how many, how much of it is foxes, how quick they are
 *   journey    how far start → bin → home is, in absolute and relative terms
 *   crossings  how many live carriageways that errand puts you across
 *   town       how much map there is to hold in your head
 *
 * Notably *not* the leash, the dog's temperament, or the cleanup hold — those
 * are the same on every level, so they cancel out of a comparison and would
 * only add a constant to every score.
 */
import { PAVEMENT, ROADS, VEHICLES, HAZARD } from '../../config/constants';
import { clamp } from '../../utils/math';
import { isStreet, spanReaches } from '../roads';
import { rectContains, type Rect } from '../level';
import { blockInteriors } from '../yards';
import type { Difficulty, LevelRating, LevelSpec, Point } from './types';

const clamp01 = (v: number): number => clamp(v, 0, 1);

/** An inclusive numeric range the generator draws from. */
export type Range = readonly [min: number, max: number];

/**
 * Everything the generator is allowed to vary for one difficulty band.
 *
 * Ranges rather than values, so two levels of the same difficulty still differ.
 * The bands overlap a little on purpose — a small hard town and a big medium
 * one should be able to exist, since the *rating* is what finally decides which
 * is which.
 */
export interface LevelBudget {
  /** Blocks across and down. Roads run between them, never at the map edge. */
  cols: Range;
  rows: Range;
  /** Interior size of one block, before the plots go in. */
  blockWidth: Range;
  blockHeight: Range;
  /**
   * How readily a candidate building slot is actually built on (0..1).
   *
   * High, and higher than it used to be. Slots were once cut from a block's
   * whole interior, so there were many and this roll was what kept a block from
   * being built out solid. They are now laid along the frontages only, so there
   * are far fewer of them and the same roll emptied the town — the variety it
   * used to provide comes from the per-plot width and gap instead.
   */
  buildChance: number;
  /**
   * How readily an ordinary street is narrowed to a back lane (0..1).
   *
   * The high street is chosen separately and there is exactly one each way (see
   * `streetWidths`); this is the only knob on the rest of the hierarchy. It
   * rises with the band for the same reason the lanes and jogs do — a harder
   * town is one it takes longer to learn your way around, and a street plan of
   * three widths reads as more tangled than one of two.
   *
   * A back street is *easier* to cross than an ordinary one, so this does not
   * make a level harder by itself; the pressure a wide high street adds is
   * accounted for in `crossingWindowMs`.
   */
  backStreetShare: number;
  /**
   * How readily a block big enough for one gets a cobbled lane through it
   * (0..1).
   *
   * Rises with the band, like every other knob here: a harder level is a
   * bigger, denser, more tangled town to find your way around, and an alley
   * cutting through a block is one more corner the bin can be round.
   */
  laneChance: number;
  /**
   * How readily a street that is not the spine jogs sideways at a junction, or
   * ends in a T or a cul-de-sac (0..1 each).
   *
   * A jog is tried first; a street that does not jog may still stop. Both rise
   * with the band for the same reason the lanes do — a harder town is one it
   * takes longer to learn your way around.
   */
  jogChance: number;
  stubChance: number;
  /** Parks to lay in the leftover ground. */
  parks: Range;
  /**
   * Litter bins in the town, the errand's included — so the floor is 1.
   *
   * Barely a difficulty knob, and deliberately so: the extras are held at the
   * errand bin's own distance from home (`landmarks.ts` → `placeBins`), so more
   * of them makes the town more legible without making the walk shorter. It
   * rises a little with the band only because a bigger town needs more of them
   * to be equally legible.
   */
  bins: Range;
  /** Vehicles alive at once. */
  maxVehicles: number;
  /** Gap between consecutive releases anywhere on the map. */
  vehicleGapMs: Range;
  /** Fraction of releases that are vans — bigger, and so harder to dodge. */
  vanShare: number;
  /** Multiplier on `VEHICLES.SPEED`. */
  speedScale: number;
  /**
   * How many of each critter.
   *
   * Pigeons are the softest of the four and their ranges are the widest, on
   * purpose: they set the dog off like anything else, but the dog's own barking
   * clears them, so a street full of them is atmosphere with a short fuse
   * rather than sustained pressure. See `critterPressure`.
   */
  cats: Range;
  foxes: Range;
  squirrels: Range;
  pigeons: Range;
  /** Multiplier on the `HAZARD.SPEED_*` band. */
  critterSpeedScale: number;
  /**
   * How long the start → bin → home errand should be, as a multiple of the
   * map's own diagonal. The landmark placer picks the arrangement that lands
   * nearest this, which is the main lever on journey length that doesn't
   * involve making the town bigger.
   */
  journeyTarget: number;
}

/**
 * The budgets.
 *
 * Read them as a progression rather than four independent rows: every knob
 * moves in the same direction across the table, so a band is "more of the same
 * pressure" rather than a different kind of level. The one exception is foxes,
 * which start at none — an easy level should be able to teach the leash without
 * anything tangling it.
 */
const BUDGETS: Record<Difficulty, LevelBudget> = {
  easy: {
    cols: [2, 3],
    rows: [2, 3],
    blockWidth: [250, 310],
    blockHeight: [230, 290],
    buildChance: 0.92,
    backStreetShare: 0.2,
    laneChance: 0.25,
    jogChance: 0.3,
    stubChance: 0.2,
    parks: [1, 2],
    bins: [2, 3],
    maxVehicles: 1,
    vehicleGapMs: [9000, 13000],
    vanShare: 0.15,
    speedScale: 0.85,
    cats: [1, 2],
    foxes: [0, 0],
    squirrels: [1, 2],
    pigeons: [2, 3],
    critterSpeedScale: 0.85,
    journeyTarget: 0.9,
  },
  medium: {
    cols: [3, 4],
    rows: [2, 3],
    blockWidth: [270, 340],
    blockHeight: [250, 320],
    buildChance: 0.9,
    backStreetShare: 0.28,
    laneChance: 0.3,
    jogChance: 0.4,
    stubChance: 0.3,
    parks: [1, 2],
    bins: [3, 3],
    maxVehicles: 2,
    vehicleGapMs: [5500, 8500],
    vanShare: 0.3,
    speedScale: 1,
    cats: [2, 3],
    foxes: [1, 1],
    squirrels: [2, 3],
    pigeons: [3, 4],
    critterSpeedScale: 1,
    journeyTarget: 1.05,
  },
  hard: {
    cols: [4, 4],
    rows: [3, 3],
    blockWidth: [270, 350],
    blockHeight: [250, 330],
    buildChance: 0.94,
    backStreetShare: 0.35,
    laneChance: 0.35,
    jogChance: 0.5,
    stubChance: 0.35,
    parks: [1, 3],
    bins: [3, 4],
    maxVehicles: 3,
    vehicleGapMs: [3500, 5500],
    vanShare: 0.4,
    speedScale: 1.1,
    cats: [3, 4],
    foxes: [2, 2],
    squirrels: [3, 4],
    pigeons: [3, 5],
    critterSpeedScale: 1.1,
    journeyTarget: 1.15,
  },
  'very-hard': {
    cols: [4, 5],
    rows: [3, 4],
    blockWidth: [270, 350],
    blockHeight: [250, 330],
    buildChance: 0.96,
    backStreetShare: 0.42,
    laneChance: 0.4,
    jogChance: 0.6,
    stubChance: 0.4,
    parks: [2, 3],
    bins: [3, 4],
    maxVehicles: 4,
    vehicleGapMs: [2600, 4200],
    vanShare: 0.5,
    speedScale: 1.25,
    cats: [4, 5],
    foxes: [3, 4],
    squirrels: [4, 5],
    pigeons: [4, 6],
    critterSpeedScale: 1.2,
    journeyTarget: 1.3,
  },
};

/** The generation budget for a band. */
export function budgetFor(difficulty: Difficulty): LevelBudget {
  return BUDGETS[difficulty];
}

/**
 * Where the bands are cut in the 0..1 score.
 *
 * Calibrated so the **hand-authored town rates medium** — it is the one level
 * anyone has actually played and tuned by feel, so it is the only fixed point
 * this scale has. `difficulty.test.ts` pins that.
 */
const BANDS: readonly { upTo: number; difficulty: Difficulty }[] = [
  { upTo: 0.3, difficulty: 'easy' },
  { upTo: 0.52, difficulty: 'medium' },
  { upTo: 0.72, difficulty: 'hard' },
  { upTo: Infinity, difficulty: 'very-hard' },
];

/** Which band a 0..1 score falls in. */
export function classifyDifficulty(score: number): Difficulty {
  return (BANDS.find((b) => score < b.upTo) ?? BANDS[BANDS.length - 1]).difficulty;
}

/** The score range a band covers, as `[lo, hi)`. Used by the generator's tuner. */
export function bandRange(difficulty: Difficulty): [number, number] {
  const i = BANDS.findIndex((b) => b.difficulty === difficulty);
  return [i === 0 ? 0 : BANDS[i - 1].upTo, BANDS[i].upTo];
}

/**
 * How many carriageways a straight leg between two points crosses.
 *
 * A straight line rather than the walked route, and that is a deliberate
 * approximation: the walked route is decided by the player, and the thing being
 * measured is how *unavoidable* the traffic is. If the bin is on the far side
 * of two streets then it is on the far side of two streets however you go.
 */
export function crossingsBetween(a: Point, b: Point, roads: LevelSpec['roads']): number {
  let n = 0;
  for (const road of roads) {
    // Lanes are not crossings. Walking down a cobbled alley exposes you to
    // nothing, so counting one would inflate the traffic pressure of a level
    // that has no more traffic in it.
    if (!isStreet(road)) continue;

    const [from, to] = road.orient === 'h' ? [a.y, b.y] : [a.x, b.x];
    if ((from - road.pos) * (to - road.pos) >= 0) continue;

    // Where along the road the leg would cross it — and whether there is any
    // road there. A street that stops halfway across town is not something you
    // cross beyond its end, however far the straight line runs.
    const t = (road.pos - from) / (to - from);
    const along = road.orient === 'h' ? a.x + (b.x - a.x) * t : a.y + (b.y - a.y) * t;
    if (spanReaches(road, along)) n++;
  }
  return n;
}

/**
 * Straight-line length of the errand: out to the bin and back to the front door.
 *
 * Measured from `start` and back to `home` rather than assuming they are the
 * same point, even though a valid level has them coincide — the rating is meant
 * to describe the level it is handed, not the level it expects.
 *
 * The detour to the grass is **not** counted. It is real distance, but the
 * player chooses when to make it and can fold it into the trip to the bin, so
 * adding it would score a walk nobody necessarily takes.
 */
export function journeyLength(spec: Errand): number {
  return legs(spec, errandBin(spec));
}

/** What measuring an errand needs to know about a level. */
type Errand = Pick<LevelSpec, 'start' | 'bins' | 'home'>;

/**
 * The bin the errand is measured to: the one that makes the round trip
 * shortest.
 *
 * A town has several (`landmarks.ts` → `placeBins`) and a player walks to
 * whichever is least trouble, so the errand is as long as the shortest way to
 * do it — rating the one the generator happened to place first would describe a
 * walk nobody takes. The extras are held at the errand bin's own distance from
 * home, so on a generated level this normally *is* `bins[0]`; it is re-derived
 * here because the rating describes the level it is handed, not the level it
 * expects.
 */
export function errandBin(spec: Errand): Point {
  let best = spec.bins[0];
  let bestLegs = Infinity;
  for (const bin of spec.bins) {
    const total = legs(spec, bin);
    if (total < bestLegs) {
      bestLegs = total;
      best = bin;
    }
  }
  return best;
}

/** Doorstep → bin → home pad, for one bin. */
function legs(spec: Errand, bin: Point): number {
  const homeCentre = { x: spec.home.x + spec.home.w / 2, y: spec.home.y + spec.home.h / 2 };
  return (
    Math.hypot(bin.x - spec.start.x, bin.y - spec.start.y) +
    Math.hypot(homeCentre.x - bin.x, homeCentre.y - bin.y)
  );
}

/**
 * The shortest time, in ms, in which a walker can get from one footway across a
 * carriageway to the other — the window a level has to leave in its traffic.
 *
 * Deliberately the **worst case**: the walker is assumed to be making only
 * `LEASH.MIN_PLAYER_PROGRESS` of their intended speed, because a dog in full
 * hyper-pull can hold them to exactly that (see the tug of war in AGENTS.md),
 * and a crossing that is only safe with a well-behaved dog is not safe. That is
 * the number the validator holds every road's traffic gap against, so it lives
 * here beside the difficulty knobs that push against it.
 */
export function crossingWindowMs(
  speed: number,
  progressFloor: number,
  /**
   * Carriageway width. Defaults to the ordinary street, which is what every
   * road was before the town had a high street — so the hand-authored level
   * and every test written against it are unchanged.
   *
   * This is where a wider street stops being a look and becomes a difficulty
   * knob: a high street is further to get across, so it demands a longer gap in
   * its traffic, and the validator holds it to that.
   */
  roadWidth: number = ROADS.WIDTH,
): number {
  const distance = roadWidth + PAVEMENT.WIDTH * 2;
  return (distance / (speed * progressFloor)) * 1000;
}

/**
 * Measure a level.
 *
 * Every part is normalised to 0..1 against a "this is as much as the game ever
 * throws at you" reference, then weighted. The weights say what the game is
 * about: traffic can end a run outright so it leads, wildlife is the constant
 * nuisance behind it, and the shape of the errand is the slow-burn third.
 */
export function rateLevel(
  spec: Omit<LevelSpec, 'rating' | 'difficulty' | 'seed'>,
): LevelRating {
  const parts = {
    traffic: trafficPressure(spec),
    critters: critterPressure(spec),
    journey: journeyPressure(spec),
    crossings: crossingPressure(spec),
    town: townPressure(spec),
  };

  const score = clamp01(
    parts.traffic * 0.32 +
      parts.critters * 0.22 +
      parts.journey * 0.18 +
      parts.crossings * 0.16 +
      parts.town * 0.12,
  );

  return { score, difficulty: classifyDifficulty(score), parts };
}

type Measured = Omit<LevelSpec, 'rating' | 'difficulty' | 'seed'>;

/** Releases per minute, fleet size and speed, weighted toward frequency. */
function trafficPressure(spec: Measured): number {
  const { schedule, loopMs, maxVehicles, speed } = spec.traffic;
  const perMinute = loopMs > 0 ? (schedule.length * 60000) / loopMs : 0;
  const rate = clamp01(perMinute / 20);
  const fleet = clamp01((maxVehicles - 1) / 3);
  const pace = clamp01((speed / VEHICLES.SPEED - 0.8) / 0.5);
  return rate * 0.5 + fleet * 0.3 + pace * 0.2;
}

/**
 * Critter pressure, counted two ways and averaged.
 *
 * Raw count *and* count per block, because neither alone is honest: eleven
 * critters in a twelve-block town are barely noticeable, and four in a
 * two-block one are everywhere. The fox share is separate because foxes are a
 * different threat — a fox stands its ground where a squirrel bolts, so the
 * dog stays locked on to one for as long as the player lets it.
 *
 * **Pigeons count for half.** They wind the dog up exactly like the rest, but
 * the dog's own barking puts them in the air and out of reach within a second
 * or so — a street of pigeons is a burst of pressure that clears itself, and
 * counting one as equal to a fox that will still be standing there in ten
 * seconds rates the level as something it isn't.
 */
const PIGEON_WEIGHT = 0.5;

function critterPressure(spec: Measured): number {
  // Blocks the walk can reach, matching where the critters actually are: they
  // are all placed inside `bounds`, so dividing by a block count that included
  // the surround would read a full town as a sparse one.
  const blocks = blockCount(spec.roads, spec.world, spec.bounds);
  const count = spec.hazards.spawns.length;
  const foxes = spec.hazards.spawns.filter((s) => s.type === 'fox').length;
  const pigeons = spec.hazards.spawns.filter((s) => s.type === 'pigeon').length;
  const weighted = count - pigeons * (1 - PIGEON_WEIGHT);

  const density = clamp01(weighted / 12) * 0.5 + clamp01(weighted / blocks / 2) * 0.5;
  const menace = clamp01(foxes / Math.max(1, count) / 0.4);
  const pace = clamp01((spec.hazards.speedMax / HAZARD.SPEED_MAX - 0.85) / 0.4);
  return density * 0.55 + menace * 0.3 + pace * 0.15;
}

/**
 * Journey length, relative to the map and in absolute units.
 *
 * Both, because they measure different complaints. Relative catches an errand
 * that criss-crosses a small town; absolute catches the long trudge across a
 * big one, which the relative figure would call unremarkable.
 *
 * The references are sized for a **round trip**. They used to be roughly half
 * this, from when a walk ended wherever home happened to be rather than back
 * at the front door; leaving them alone when the errand doubled would have
 * pushed every level up a band without anything about it getting harder — the
 * hand-authored town went straight from medium to hard on that change alone.
 */
function journeyPressure(spec: Measured): number {
  // The **playable** diagonal. The grid now carries on past the walk (see
  // `levelgen/surround.ts`), and measuring against the whole of it would call
  // the same errand easier on a level that merely has more scenery drawn round
  // it — which is not a difficulty decision anyone made.
  const diagonal = Math.hypot(spec.bounds.w, spec.bounds.h);
  const total = journeyLength(spec);
  return clamp01(total / (diagonal * 2.8)) * 0.6 + clamp01(total / JOURNEY_REFERENCE) * 0.4;
}

/**
 * The errand length that counts as "as far as this game ever asks you to walk".
 *
 * Sized against the biggest playable rectangle the budgets can produce, so the
 * absolute term still discriminates at the top of the range rather than
 * saturating on every level above medium. It was 4200, from when a very-hard
 * town was about 1720 × 1150; the playable rectangle is now roughly double that
 * area and the errand grew with it.
 */
const JOURNEY_REFERENCE = 6500;

/**
 * Carriageways crossed on the errand.
 *
 * Counted **both ways**, and normalised for it: a round trip crosses each street
 * on the way out and again on the way back, which really is twice the exposure
 * to traffic, so the count is honest and it is the reference that has to
 * account for the doubling.
 */
function crossingPressure(spec: Measured): number {
  const homeCentre = { x: spec.home.x + spec.home.w / 2, y: spec.home.y + spec.home.h / 2 };
  // The same bin the journey is measured to, or the two halves of the rating
  // would describe two different walks.
  const bin = errandBin(spec);
  const n =
    crossingsBetween(spec.start, bin, spec.roads) + crossingsBetween(bin, homeCentre, spec.roads);
  return clamp01(n / 8);
}

/**
 * How much town there is: block count and sheer ground area.
 *
 * Both measured over `bounds` rather than the whole grid. The surround is town
 * you can look at and never walk into, so it is not more map to hold in your
 * head — and counting it would hand every level the same saturated score,
 * turning this term into a constant added to all four bands.
 */
function townPressure(spec: Measured): number {
  const blocks = blockCount(spec.roads, spec.world, spec.bounds);
  const area = spec.bounds.w * spec.bounds.h;
  // Both references are sized off the largest playable rectangle the budgets can
  // now produce — about 2600 × 1900, or twenty blocks. They were 10 and
  // 1_760_000, from when a very-hard town was 1720 × 1150; left alone, every
  // level from hard upward pinned this term at 1.0 and it stopped saying
  // anything about the difference between them.
  return clamp01((blocks - 2) / 24) * 0.5 + clamp01(area / 5_000_000) * 0.5;
}

/**
 * How many blocks the ways cut the map into.
 *
 * This used to be `(v + 1) * (h + 1)` — one more than the roads in each
 * direction — which is only true of a grid, and is wrong twice over now that it
 * isn't one. A street that jogs is stored as two collinear segments, so the
 * formula counts it as two streets and invents a block that does not exist; and
 * a street that stops divides nothing past its end, so two blocks the formula
 * counts separately are really one.
 *
 * Both errors feed the difficulty score, so it is measured off the same
 * decomposition the town is actually built from rather than a count of roads.
 */
export function blockCount(
  roads: LevelSpec['roads'],
  world: { width: number; height: number },
  /**
   * Count only the blocks inside here. Decomposition still has to run over the
   * whole grid — a block's shape depends on every road around it, including the
   * ones out in the surround — so this filters the result rather than the input.
   */
  bounds?: Rect,
): number {
  const blocks = blockInteriors(world, roads, ROADS.WIDTH, PAVEMENT.WIDTH, 0);
  return bounds ? blocks.filter((b) => rectContains(bounds, b)).length : blocks.length;
}
